/**
 * Layer Registry Service
 * Singleton service for managing and initializing all map layers
 * Integrates with MapManager and ImagePreloader
 *
 * NOTE: API-based layers (incidents, ports, militaryLocations) are now managed
 * by useApiLayers hook via React Query. The legacy initializers are kept for
 * backward compatibility but should not be used for new code.
 */

import type { Map as MapboxMap } from 'mapbox-gl';
import { addEEZ } from './layers/eezLayer';
import { addIncidents, addIncidentsLayer, type Incident } from './layers/incidentsLayer';
import {
  addAirForceLocations,
  addCommandControl,
  addNuclearSites,
  addOverseasPLAPlanBases,
  addPLAGroundForces,
  addPLAInstitutes,
  addPLAPlanBases,
} from './layers/militaryLayers';
import {
  addMilitaryLocations,
  addMilitaryLocationsLayer,
  type MilitaryLocation,
} from './layers/militaryLocationsLayer';
import { addPorts, addPortsLayer, type Port } from './layers/portsLayer';
import { addPowerPlants } from './layers/powerPlantsLayer';
import { addStarterPopups } from './layers/starterPopups';
import { addCorridorLayer } from './layers/corridorLayer';
import { addClouds, addPressure, addTemperature, addWind } from './layers/weatherLayers';
import { mapManager } from './MapManager';

type LayerInitializer = (map: MapboxMap | null, ...args: unknown[]) => Promise<void> | void;

interface LayerConfig {
  name: string;
  initializer: LayerInitializer;
  dependencies?: string[];
  /** Whether this layer requires authentication token */
  requiresAuth?: boolean;
  /** Layer IDs created by this layer config (for cleanup) */
  layerIds?: string[];
  /** Source IDs created by this layer config (for cleanup) */
  sourceIds?: string[];
}

class LayerRegistry {
  private static instance: LayerRegistry;
  private layers: Map<string, LayerConfig> = new Map();
  private initialized: Set<string> = new Set();

  private constructor() {
    // Private constructor to enforce singleton pattern
    this.registerDefaultLayers();
  }

  /**
   * Get the singleton instance
   */
  static getInstance(): LayerRegistry {
    if (!LayerRegistry.instance) {
      LayerRegistry.instance = new LayerRegistry();
    }
    return LayerRegistry.instance;
  }

  /**
   * Register default layers
   */
  private registerDefaultLayers(): void {
    // API-based layers - now support both legacy (getToken) and data-driven modes
    // Data-driven mode is preferred and used by useApiLayers hook
    this.registerLayer('incidents', {
      name: 'Incidents',
      initializer: (
        map: MapboxMap | null,
        dataOrGetToken: Incident[] | (() => Promise<string>),
        setLoading?: (loading: boolean) => void,
      ) => {
        // Data-driven mode: array of incidents passed directly
        if (Array.isArray(dataOrGetToken)) {
          addIncidentsLayer(map, dataOrGetToken);
        } else {
          // Legacy mode: getToken function passed
          addIncidents(map, dataOrGetToken, setLoading);
        }
      },
    });

    this.registerLayer('ports', {
      name: 'Ports',
      initializer: (map: MapboxMap | null, dataOrGetToken: Port[] | (() => Promise<string>)) => {
        // Data-driven mode: array of ports passed directly
        if (Array.isArray(dataOrGetToken)) {
          addPortsLayer(map, dataOrGetToken);
        } else {
          // Legacy mode: getToken function passed
          addPorts(map, dataOrGetToken);
        }
      },
    });

    this.registerLayer('militaryLocations', {
      name: 'Military Locations',
      initializer: (map: MapboxMap | null, dataOrGetToken: MilitaryLocation[] | (() => Promise<string>)) => {
        // Data-driven mode: array of locations passed directly
        if (Array.isArray(dataOrGetToken)) {
          addMilitaryLocationsLayer(map, dataOrGetToken);
        } else {
          // Legacy mode: getToken function passed
          addMilitaryLocations(map, dataOrGetToken);
        }
      },
    });

    // CSV-based military layers
    this.registerLayer('airForceLocations', {
      name: 'Air Force Locations',
      initializer: (map: MapboxMap | null) => addAirForceLocations(map),
    });

    this.registerLayer('plaInstitutes', {
      name: 'PLA Institutes',
      initializer: (map: MapboxMap | null) => addPLAInstitutes(map),
    });

    this.registerLayer('commandControl', {
      name: 'Command Control',
      initializer: (map: MapboxMap | null) => addCommandControl(map),
    });

    this.registerLayer('nuclearSites', {
      name: 'Nuclear Sites',
      initializer: (map: MapboxMap | null) => addNuclearSites(map),
    });

    this.registerLayer('plaGroundForces', {
      name: 'PLA Ground Forces',
      initializer: (map: MapboxMap | null) => addPLAGroundForces(map),
    });

    this.registerLayer('plaPlanBases', {
      name: 'PLA Plan Bases',
      initializer: (map: MapboxMap | null) => addPLAPlanBases(map),
    });

    this.registerLayer('overseasPLAPlanBases', {
      name: 'Overseas PLA Plan Bases',
      initializer: (map: MapboxMap | null) => addOverseasPLAPlanBases(map),
    });

    // Power plants
    this.registerLayer('powerPlants', {
      name: 'Power Plants',
      initializer: (map: MapboxMap | null) => addPowerPlants(map),
    });

    // Weather layers
    this.registerLayer('clouds', {
      name: 'Clouds',
      initializer: (map: MapboxMap | null) => addClouds(map),
    });

    this.registerLayer('temperature', {
      name: 'Temperature',
      initializer: (map: MapboxMap | null) => addTemperature(map),
    });

    this.registerLayer('pressure', {
      name: 'Atmospheric Pressure',
      initializer: (map: MapboxMap | null) => addPressure(map),
    });

    this.registerLayer('wind', {
      name: 'Wind',
      initializer: (map: MapboxMap | null) => addWind(map),
    });

    // EEZ
    this.registerLayer('eez', {
      name: 'EEZ',
      initializer: (map: MapboxMap | null) => addEEZ(map),
    });

    // Corridor Intelligence
    this.registerLayer('corridor', {
      name: 'Cocaine Corridor',
      initializer: (map: MapboxMap | null) => addCorridorLayer(map),
    });

    // Starter popups
    this.registerLayer('starterPopups', {
      name: 'Starter Popups',
      initializer: (map: MapboxMap | null, onPopupClick?: (type: string) => void) => {
        addStarterPopups(map, onPopupClick);
      },
    });
  }

  /**
   * Register a layer
   */
  registerLayer(name: string, config: LayerConfig): void {
    this.layers.set(name, config);
  }

  /**
   * Get a layer by name
   */
  getLayer(name: string): LayerConfig | undefined {
    return this.layers.get(name);
  }

  /**
   * Initialize a single layer
   * Ensures MapManager is initialized with the map instance
   */
  async initializeLayer(name: string, map: MapboxMap | null, ...args: unknown[]): Promise<void> {
    const layer = this.layers.get(name);
    if (!layer) {
      console.warn(`Layer ${name} not found in registry`);
      return;
    }

    if (this.initialized.has(name)) {
      return; // Already initialized
    }

    // Ensure MapManager is initialized with the map
    if (map && mapManager.getMap() !== map) {
      mapManager.initialize(map);
    }

    // Check dependencies
    if (layer.dependencies) {
      for (const dep of layer.dependencies) {
        if (!this.initialized.has(dep)) {
          await this.initializeLayer(dep, map, ...args);
        }
      }
    }

    try {
      // Preload any required images for this layer if needed
      // (This is a placeholder - individual layers can use ImagePreloader as needed)

      await layer.initializer(map, ...args);
      this.initialized.add(name);
    } catch (error) {
      console.error(`Error initializing layer ${name}:`, error);
      // Retry logic could be added here if needed
    }
  }

  /**
   * Initialize all startup layers
   * Replaces the old addStartupLayers function
   */
  async initializeAllLayers(
    map: MapboxMap | null,
    getToken: () => Promise<string>,
    setLoading?: (loading: boolean) => void,
    onPopupClick?: (type: string) => void,
  ): Promise<void> {
    const layerOrder = [
      'incidents',
      'powerPlants',
      'airForceLocations',
      'plaInstitutes',
      'commandControl',
      'nuclearSites',
      'plaGroundForces',
      'plaPlanBases',
      'overseasPLAPlanBases',
      'ports',
      'militaryLocations',
      'eez',
      'wind',
      'pressure',
      'clouds',
      'temperature',
      'starterPopups',
    ];

    for (const layerName of layerOrder) {
      if (layerName === 'incidents' || layerName === 'ports' || layerName === 'militaryLocations') {
        await this.initializeLayer(layerName, map, getToken, setLoading);
      } else if (layerName === 'starterPopups') {
        await this.initializeLayer(layerName, map, onPopupClick);
      } else {
        await this.initializeLayer(layerName, map);
      }
    }
  }

  /**
   * Reset initialization state (useful for testing or re-initialization)
   */
  reset(): void {
    this.initialized.clear();
  }

  /**
   * Mark a specific layer as not initialized (for re-initialization after style reload)
   */
  markNotInitialized(name: string): void {
    this.initialized.delete(name);
  }

  /**
   * Check if a layer is initialized
   */
  isInitialized(name: string): boolean {
    return this.initialized.has(name);
  }
}

// Export singleton instance
export const layerRegistry = LayerRegistry.getInstance();
